import { put, select, takeLatest } from 'redux-saga/effects';
import {
  FORGOT_PASSWORD,
  FORGOT_PASSWORD_FAILED,
  FORGOT_PASSWORD_SUCCESS,
  GET_AUTHORIZATION_INFO,
  GET_AUTHORIZATION_INFO_COMPLETE,
  GET_AUTHORIZATION_INFO_FAILED,
  KEA_SIGN_IN,
  KEA_SIGN_IN_FAILED,
  KEA_SIGN_IN_SUCCESS,
  KEA_SIGN_IN_WITH_SECRET,
  KEA_SIGN_IN_WITH_SECRET_FAILED,
  KEA_SIGN_IN_WITH_SECRET_SUCCESS,
  SIGNOUT_USER,
  ON_HIDE_LOADER,
  ON_SHOW_LOADER,
  SUBMIT_NEW_PASSWORD,
  SUBMIT_NEW_PASSWORD_SUCCESS,
  CREATE_LEAD,
  CREATE_LEAD_SUCCESS,
  CREATE_LEAD_FAILED,
} from 'constants/ActionTypes';

import { rmToken, setToken, tokenKey } from 'util/authUtils';
import { m, wsClient } from 'util/appUtils';
import { isWebserviceAuthorizationFailure } from 'util/webserviceErrors';
import qs from 'qs';
import { rewriteAlertBanners } from '../actions';

const wsBaseUri = window.WS_BASE_URI;

async function postRequest(url, body) {
  const options = {
    method: m.POST,
    credentials: 'include',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(body),
  };
  return fetch(url, options);
}

function userSignOut() {
  rmToken();
  window.location.assign('/signin');
}

function* signIn({ body, url, queryParams = {} }) {
  const response = yield postRequest(url, body);
  const { mode } = queryParams;
  const isEmbedded = (mode === 'embedded');
  if (response.status === 200) {
    const sessionToken = response.headers.get(tokenKey);
    setToken(sessionToken);
    const appAddress = ['/', qs.stringify(queryParams)].join('?');
    window.location.assign(appAddress);
  } else {
    rmToken();
    const prefix = isEmbedded ? '/embedded' : '';
    window.location.assign(`${prefix}/signin?banner=SING_IN_FAILED`);
    throw new Error(`${response.status} — ${response.statusText}`);
  }
}

function* resetPasswordRequest({ payload }) {
  const bannersActions = yield select((store) => store.settings.alerts.actions);
  yield put({ type: ON_SHOW_LOADER });
  try {
    const url = `${wsBaseUri}system/password/reset?email=${payload.email}`;
    const response = yield fetch(url);
    if (response.status !== 200) {
      const bannerAction = bannersActions.PASSWORD_RESET_FAILED;
      yield put(rewriteAlertBanners([bannerAction]));
      yield put({ type: FORGOT_PASSWORD_FAILED, payload: response.error });
    } else {
      const bannerAction = bannersActions.PASSWORD_RESET;
      yield put(rewriteAlertBanners([bannerAction]));
      yield put({ type: FORGOT_PASSWORD_SUCCESS });
    }
  } catch (err) {
    const bannerAction = bannersActions.PASSWORD_RESET_FAILED;
    yield put(rewriteAlertBanners([bannerAction]));
    yield put({ type: FORGOT_PASSWORD_FAILED, payload: err });
  }
  yield put({ type: ON_HIDE_LOADER });
}

function* submitNewPassword({ payload }) {
  const bannersActions = yield select((store) => store.settings.alerts.actions);
  yield put({ type: ON_SHOW_LOADER });
  try {
    const url = `${wsBaseUri}system/password/confirm`;
    const response = yield postRequest(url, payload);
    if (response.status !== 200) {
      const bannerAction = bannersActions.PASSWORD_RESET_FAILED;
      yield put(rewriteAlertBanners([bannerAction]));
      yield put({ type: FORGOT_PASSWORD_FAILED, payload: response.error });
    } else {
      window.location.assign('/signin?banner=PASSWORD_UPDATED_SUCCESSFULLY');
      yield put({ type: SUBMIT_NEW_PASSWORD_SUCCESS });
    }
  } catch (err) {
    const bannerAction = bannersActions.PASSWORD_RESET_FAILED;
    yield put(rewriteAlertBanners([bannerAction]));
    yield put({ type: FORGOT_PASSWORD_FAILED, payload: err });
  }
  yield put({ type: ON_HIDE_LOADER });
}

function* keaSignIn({ payload }) {
  try {
    const { email, password, details = {}, ...queryParams } = payload;

    const body = { email, password, details };
    const url = `${wsBaseUri}auth/login`;
    yield signIn({ body, url, queryParams });

    yield put({ type: KEA_SIGN_IN_SUCCESS });
  } catch (err) {
    yield put({ type: KEA_SIGN_IN_FAILED, payload: err });
  }
}

function* keaSignInWithSecret({ payload }) {
  try {
    const { domain: token, secret, appId } = payload;
    const body = { token, secret, appId };
    const url = `${wsBaseUri}auth/login/token`;
    yield signIn({
      body,
      url,
      queryParams: { mode: 'embedded', appId },
    });

    yield put({ type: KEA_SIGN_IN_WITH_SECRET_SUCCESS });
  } catch (err) {
    yield put({ type: KEA_SIGN_IN_WITH_SECRET_FAILED, payload: err });
  }
}

function* getAuthorization() {
  try {
    const { account, shops } = yield wsClient.prepareRequest(m.GET, 'account');
    const result = {
      account,
      shopList: [],
      shopMap: {},
    };

    if (shops) {
      result.shopList = Object.keys(shops).map((shop) => shops[shop]);
      result.shopMap = shops;
    }

    yield put({ type: GET_AUTHORIZATION_INFO_COMPLETE, payload: result });
  } catch (err) {
    if (isWebserviceAuthorizationFailure(err)) {
      rmToken();
      window.location.reload();
      return;
    }
    yield put({ type: GET_AUTHORIZATION_INFO_FAILED, payload: err });
  }
}

function* createLead({ payload }) {
  try {
    const response = yield wsClient.createLead(payload);
    yield put({ type: CREATE_LEAD_SUCCESS });
  } catch (err) {
    yield put({ type: CREATE_LEAD_FAILED, payload: err });
  }
}

export default function* authSaga() {
  yield takeLatest(SIGNOUT_USER, userSignOut);
  yield takeLatest(KEA_SIGN_IN, keaSignIn);
  yield takeLatest(KEA_SIGN_IN_WITH_SECRET, keaSignInWithSecret);
  yield takeLatest(GET_AUTHORIZATION_INFO, getAuthorization);
  yield takeLatest(FORGOT_PASSWORD, resetPasswordRequest);
  yield takeLatest(SUBMIT_NEW_PASSWORD, submitNewPassword);
  yield takeLatest(CREATE_LEAD, createLead);
}
